import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SpeedInsights } from "@vercel/speed-insights/react";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import SellerReport from "./pages/SellerReport";
import PublicNorthReport from "./pages/PublicNorthReport";
import CMAReport from "./pages/CMAReport";
import PublicCMA from "./pages/PublicCMA";
import BuyerReport from "./pages/BuyerReport";
import PublicBuyerReport from "./pages/PublicBuyerReport";
import Demo from "./pages/Demo";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import Settings from "./pages/Settings";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import AcceptInvitation from "./pages/AcceptInvitation";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./pages/AdminDashboard";
import SelectPlan from "./pages/SelectPlan";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ExportData from "./pages/ExportData";
import Spotlight from "./pages/Spotlight";
import DemoSpotlight from "./pages/DemoSpotlight";
import SpotlightManage from "./pages/SpotlightManage";
import Activity from "./pages/Activity";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { BrandColorProvider } from "./components/BrandColorProvider";
import { CookieConsent } from "./components/CookieConsent";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SupabaseHealthProvider } from "./components/SupabaseHealthProvider";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { isSupabaseTimeout } from "@/lib/fetchWithTimeout";
import { supabaseHealth } from "@/lib/supabaseHealth";
import { TourProvider } from "./components/tour/TourProvider";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isSupabaseTimeout(error)) {
        supabaseHealth.forceCheck();
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (previously cacheTime)
      refetchOnWindowFocus: false, // Don't refetch when tab becomes visible
      refetchOnMount: false, // Don't refetch when component remounts (prevents reload on tab return)
      refetchOnReconnect: true, // Still refetch on network reconnect
      retry: (failureCount, error) => {
        // Don't retry timeout errors — they'd just timeout again
        if (isSupabaseTimeout(error)) return false;
        return failureCount < 1;
      },
    },
  },
});

function App() {
  // CRITICAL: Clear all React Query cache on auth state changes to prevent cross-user data leakage
  // Track current user to only clear cache when user actually changes (not on session restore)
  const lastUserIdRef = React.useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') {
        const newUserId = session?.user?.id ?? null;
        const isInitialLoad = lastUserIdRef.current === undefined;
        const userChanged = lastUserIdRef.current !== undefined && lastUserIdRef.current !== newUserId;
        lastUserIdRef.current = newUserId;

        // Only nuke the cache when the user actually changes (login/logout/switch)
        // Skip on initial page load (session restore) and token refreshes for the same user
        if (userChanged) {
          logger.error('CRITICAL: User changed - clearing ALL React Query cache to prevent data leakage', {
            event,
            userId: newUserId,
            email: session?.user?.email,
          });
          queryClient.clear();
          queryClient.removeQueries();
          queryClient.resetQueries();
        } else if (isInitialLoad) {
          // First auth event on page load — just record the user, don't clear
          logger.debug('Auth initialized (session restore)', { event, userId: newUserId });
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  return (
  <QueryClientProvider client={queryClient}>
    <SupabaseHealthProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ImpersonationProvider>
          <ImpersonationBanner />
          <TourProvider>
            <BrandColorProvider>
              <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/select-plan" element={<ProtectedRoute><SelectPlan /></ProtectedRoute>} />
          <Route path="/seller-report/:id" element={<ProtectedRoute><SellerReport /></ProtectedRoute>} />
          <Route path="/north/:id" element={<PublicNorthReport />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/demo-spotlight" element={<DemoSpotlight />} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
          <Route path="/export-data" element={<ProtectedRoute><ExportData /></ProtectedRoute>} />
          <Route path="/spotlight/manage" element={<ProtectedRoute><SpotlightManage /></ProtectedRoute>} />
          <Route path="/spotlight/:teamId" element={<Spotlight />} />
          <Route path="/s/:slug" element={<Spotlight />} />
          <Route path="/activity" element={<ProtectedRoute><Activity /></ProtectedRoute>} />
          <Route path="/cma/public/:id" element={<PublicCMA />} />
          <Route path="/cma/:id" element={<ProtectedRoute><CMAReport /></ProtectedRoute>} />
          <Route path="/buyer-report/public/:id" element={<PublicBuyerReport />} />
          <Route path="/buyer-report/:id" element={<ProtectedRoute><BuyerReport /></ProtectedRoute>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <CookieConsent />
            </BrandColorProvider>
          </TourProvider>
        </ImpersonationProvider>
      </BrowserRouter>
      <SpeedInsights />
    </TooltipProvider>
    </SupabaseHealthProvider>
  </QueryClientProvider>
);
}

export default App;
